import React, { useState } from 'react';
import posthog from 'posthog-js';
import { Icon } from './ui/Icon.jsx';
import { LangToggle } from './ui/LangToggle.jsx';
import { supaFetch } from '../lib/supabase.js';
import { SUPABASE_URL, SUPABASE_KEY, LOGO_URL } from '../lib/constants.js';
import { COLORS, FONT, input, button } from '../lib/theme.js';

const EMPTY = { company_name: '', contact_name: '', email: '', phone: '', tax_id: '' };

// ─── PUBLIC: RETAILER PRICING REGISTRATION (/register) ───────────────────────
// Direct-shareable version of the in-catalogue "Apply for Wholesale Access"
// modal. Same table (retailer_applications), same admin approval flow —
// just reachable via a plain link for WhatsApp/social campaigns.
export default function RetailerRegister() {
  const [lang, setLang] = useState(() => localStorage.getItem('sedra_lang') || 'ar');
  const ar  = lang === 'ar';
  const dir = ar ? 'rtl' : 'ltr';

  const [form, setForm]             = useState({ ...EMPTY });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState(false);
  const [err, setErr]               = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true); setErr('');
    try {
      await supaFetch('POST', 'retailer_applications', {
        company_name:       form.company_name.trim(),
        contact_name:       form.contact_name.trim(),
        email:              form.email.trim().toLowerCase(),
        phone:              form.phone.trim(),
        tax_id:             form.tax_id.trim() || null,
        preferred_language: lang,
      }, '', { 'Prefer': 'return=minimal' });

      // Notify admin — best-effort, never blocks the success screen
      fetch(`${SUPABASE_URL}/functions/v1/notify-new-application`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_KEY },
        body:    JSON.stringify({
          company_name: form.company_name.trim(),
          contact_name: form.contact_name.trim(),
          email:        form.email.trim().toLowerCase(),
          phone:        form.phone.trim(),
        }),
      }).catch(() => {});

      posthog.capture('retailer_application_submitted', {
        preferred_language: lang,
        has_tax_id: Boolean(form.tax_id.trim()),
      });
      setSuccess(true);
    } catch (e2) {
      posthog.captureException(e2, { flow: 'retailer_application' });
      setErr(ar ? 'حدث خطأ — حاول مرة أخرى' : 'Something went wrong — please try again');
    }
    setSubmitting(false);
  };

  const fields = [
    { key: 'company_name', label: ar ? 'اسم المنشأة / الشركة' : 'Company name', required: true },
    { key: 'contact_name', label: ar ? 'اسم المسؤول' : 'Contact name', required: true },
    { key: 'email',        label: ar ? 'البريد الإلكتروني' : 'Email', required: true, type: 'email' },
    { key: 'phone',        label: ar ? 'رقم الهاتف' : 'Phone number', required: true, type: 'tel' },
    { key: 'tax_id',       label: ar ? 'السجل التجاري / الرقم الضريبي (اختياري)' : 'Tax ID / Trade license (optional)', required: false },
  ];

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(160deg,#1f271c 0%,#2c3527 45%,#3A4534 100%)`, fontFamily: FONT, direction: dir, padding: '40px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: 520, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={LOGO_URL} alt="Sedra Home" style={{ width: 80, borderRadius: 10, marginBottom: 12, filter: 'drop-shadow(0 6px 20px rgba(0,0,0,0.4))' }} />
          <h1 style={{ margin: '0 0 8px', fontSize: 22, color: '#fff' }}>
            {ar ? 'سجّل للحصول على أسعار الجملة' : 'Register for Wholesale Pricing'}
          </h1>
          <p style={{ margin: 0, fontSize: 13.5, color: 'rgba(210,225,195,0.85)', lineHeight: 1.8 }}>
            {ar
              ? 'قدّم بياناتك وسيصلك رابط الكتالوج الخاص بك بالأسعار فور الموافقة — عادة خلال ٢٤ ساعة.'
              : 'Submit your details and receive your personal priced catalogue link on approval — usually within 24 hours.'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <LangToggle lang={lang} setLang={setLang} variant="dark" size="sm" />
          </div>
        </div>

        {success ? (
          <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: '38px 30px', textAlign: 'center', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: COLORS.success }}><Icon name="CheckCircle2" size={44} /></div>
            <div style={{ fontWeight: 800, fontSize: 17, color: COLORS.brandDark, marginBottom: 8 }}>
              {ar ? 'تم استلام طلبك!' : 'Application received!'}
            </div>
            <div style={{ fontSize: 13.5, color: COLORS.textMuted, lineHeight: 1.8 }}>
              {ar
                ? 'سيراجع فريقنا طلبك ويرسل لك رابط الكتالوج الخاص بك عبر البريد الإلكتروني أو واتساب.'
                : 'Our team will review your application and send your personal catalogue link by email or WhatsApp.'}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: '28px 26px', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}>
            {fields.map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: COLORS.brandDark, marginBottom: 5 }}>
                  {f.label}{f.required && <span style={{ color: COLORS.danger }}> *</span>}
                </label>
                <input style={input} type={f.type || 'text'} required={f.required} value={form[f.key]}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
              </div>
            ))}
            {err && <div style={{ padding: '9px 14px', background: COLORS.dangerBg, color: COLORS.danger, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{err}</div>}
            <button type="submit" disabled={submitting}
              style={{ ...button(submitting ? '#aaa' : COLORS.brand), width: '100%', padding: '13px 0', fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {!submitting && <Icon name="KeyRound" size={15} />}
              {submitting ? (ar ? 'جارٍ الإرسال…' : 'Submitting…') : (ar ? 'إرسال طلب التسجيل' : 'Submit registration')}
            </button>
            <div style={{ marginTop: 12, fontSize: 11.5, color: COLORS.textMuted, textAlign: 'center' }}>
              {ar ? 'تاجر بالفعل؟ استخدم رابط الكتالوج الخاص بك.' : 'Already a retailer? Use your personal catalogue link.'}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
